import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Send, Mail, CheckCircle, Upload, FileText, User, Building, Search, DollarSign, Wrench, Package, MessageCircle, Pencil } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import Button from './shared/Button';
import { supabase } from '../supabaseClient';
import { showNotification, showError } from '../utils/notificationHandler';

// inline: render just the form (for the Contact Us page) instead of a modal.
const ContactForm = ({ isOpen, onClose, isPublic = false, inline = false }) => {
  const [formData, setFormData] = useState({
    subject: '',
    priority: 'medium',
    message: '',
    customSubject: ''
  });
  const [userInfo, setUserInfo] = useState({
    fullName: '',
    email: '',
    companyName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const captchaRef = useRef(null);

  const subjectOptions = [
    { value: 'product-inquiry', label: 'Product Inquiry', icon: Search },
    { value: 'pricing-request', label: 'Pricing Request', icon: DollarSign },
    { value: 'technical-support', label: 'Technical Support', icon: Wrench },
    { value: 'order-inquiry', label: 'Order Inquiry', icon: Package },
    { value: 'account-support', label: 'Account Support', icon: User },
    { value: 'general-inquiry', label: 'General Inquiry', icon: MessageCircle },
    { value: 'custom', label: 'Other (Custom)', icon: Pencil }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-green-500', textColor: 'text-green-700' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-500', textColor: 'text-yellow-700' },
    { value: 'high', label: 'High', color: 'bg-orange-500', textColor: 'text-orange-700' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-500', textColor: 'text-red-700' }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (!isPublic) {
        fetchUserInfo();
      }
      setFormData({
        subject: '',
        priority: 'medium',
        message: '',
        customSubject: ''
      });
      setUserInfo({
        fullName: '',
        email: '',
        companyName: ''
      });
      setAttachment(null);
      setShowSuccess(false);
      setCaptchaToken(null);
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
    }
  }, [isOpen, isPublic]);

  const fetchUserInfo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('full_name, email, company_name')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setUserInfo({
          fullName: data.full_name || '',
          email: data.email || user.email || '',
          companyName: data.company_name || ''
        });
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      showError(error, 'Failed to load user information');
    }
  };

  const handleUserInfoChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showError(null, 'File size must be less than 10MB');
        return;
      }
      setAttachment(file);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const validateForm = () => {
    if (isPublic) {
      if (!userInfo.companyName.trim()) {
        showError(null, 'Please enter your company name');
        return false;
      }
      if (!userInfo.fullName.trim()) {
        showError(null, 'Please enter your name');
        return false;
      }
      if (!userInfo.email.trim() || !userInfo.email.includes('@')) {
        showError(null, 'Please enter a valid email address');
        return false;
      }
      if (!captchaToken) {
        showError(null, 'Please complete the captcha');
        return false;
      }
    }
    if (!formData.subject) {
      showError(null, 'Please select a subject');
      return false;
    }
    if (formData.subject === 'custom' && !formData.customSubject.trim()) {
      showError(null, 'Please enter a custom subject');
      return false;
    }
    if (!formData.message.trim()) {
      showError(null, 'Please enter a message');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Prepare the email data
      const emailData = {
        userInfo,
        subject: formData.subject === 'custom' ? formData.customSubject : 
                subjectOptions.find(opt => opt.value === formData.subject)?.label || '',
        priority: formData.priority,
        message: formData.message,
        currentPage: window.location.pathname,
        timestamp: new Date().toISOString()
      };

      // Handle file attachment if present
      let attachmentData = null;
      if (attachment) {
        // Convert file to base64 for email sending
        const reader = new FileReader();
        attachmentData = await new Promise((resolve) => {
          reader.onload = () => {
            resolve({
              name: attachment.name,
              type: attachment.type,
              size: attachment.size,
              data: reader.result.split(',')[1] // Remove data:type;base64, prefix
            });
          };
          reader.readAsDataURL(attachment);
        });
      }

      // Try Supabase Edge Function first, fallback to direct email if it fails
      try {
        const { error } = await supabase.functions.invoke('send-contact-form', {
          body: {
            ...emailData,
            attachment: attachmentData
          }
        });

        if (error) throw error;

        setShowSuccess(true);
        showNotification('Your message has been sent successfully!', 'success');
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } catch (functionError) {
        console.log('Edge function failed, trying fallback method...', functionError);
        
        // Fallback: Create a simple email using existing infrastructure
        const fallbackEmailData = {
          to: 'sales@mhs.com.au',
          subject: `[Partner Portal] ${emailData.subject} - ${emailData.userInfo.companyName}`,
          body: `
New Contact Form Submission

Company: ${emailData.userInfo.companyName}
Contact Person: ${emailData.userInfo.fullName}
Email: ${emailData.userInfo.email}
Subject: ${emailData.subject}
Priority: ${emailData.priority.toUpperCase()}

Message:
${emailData.message}

Submitted: ${new Date(emailData.timestamp).toLocaleString('en-AU', { timeZone: 'Australia/Perth' })} (AWST)
Page Context: ${emailData.currentPage}
Source: Partner Portal Contact Form

${attachment ? `\nNote: File attachment "${attachment.name}" was included but cannot be sent via fallback method. Please contact the user directly for the file.` : ''}
          `.trim()
        };

        // Use mailto as ultimate fallback
        const mailtoUrl = `mailto:${fallbackEmailData.to}?subject=${encodeURIComponent(fallbackEmailData.subject)}&body=${encodeURIComponent(fallbackEmailData.body)}`;
        
        // Try to open mailto (this will work on most systems)
        const mailWindow = window.open(mailtoUrl, '_blank');
        
        // Check if mailto opened successfully
        setTimeout(() => {
          if (mailWindow) {
            mailWindow.close();
          }
          
          setShowSuccess(true);
          showNotification('Message prepared! Please send the email that opened in your default email client.', 'success');
          
          // Auto-close after 3 seconds for fallback
          setTimeout(() => {
            onClose();
          }, 3000);
        }, 1000);
      }

    } catch (error) {
      console.error('Error sending contact form:', error);
      showError(error, 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';
  const inputClass = 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent';
  const inputIcon = 'absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none';
  const chipClass = (selected) => `rounded-lg border text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
    selected
      ? 'border-brand bg-brand/10 text-gray-900 font-medium'
      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
  }`;

  const body = showSuccess ? (
    <div className="p-8 text-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", damping: 15, stiffness: 300 }}
        className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4"
      >
        <CheckCircle className="h-8 w-8 text-green-600" aria-hidden="true" />
      </motion.div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Message Sent!</h3>
      <p className="text-sm text-gray-500">Our sales team will get back to you soon.</p>
    </div>
  ) : (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* User Info - Editable for Public, Display for Authenticated */}
        {isPublic ? (
          <div className="space-y-4">
            <div>
              <label htmlFor="contact-company" className={labelClass}>Company Name *</label>
              <div className="relative">
                <Building className={inputIcon} aria-hidden="true" />
                <input
                  id="contact-company"
                  type="text"
                  value={userInfo.companyName}
                  onChange={(e) => handleUserInfoChange('companyName', e.target.value)}
                  placeholder="Your company name"
                  required
                  className={`${inputClass} pl-9`}
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-name" className={labelClass}>Your Name *</label>
              <div className="relative">
                <User className={inputIcon} aria-hidden="true" />
                <input
                  id="contact-name"
                  type="text"
                  value={userInfo.fullName}
                  onChange={(e) => handleUserInfoChange('fullName', e.target.value)}
                  placeholder="Your full name"
                  required
                  className={`${inputClass} pl-9`}
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-email" className={labelClass}>Email Address *</label>
              <div className="relative">
                <Mail className={inputIcon} aria-hidden="true" />
                <input
                  id="contact-email"
                  type="email"
                  value={userInfo.email}
                  onChange={(e) => handleUserInfoChange('email', e.target.value)}
                  placeholder="your.email@company.com"
                  required
                  className={`${inputClass} pl-9`}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm">
            <span className="flex items-center gap-2 text-gray-900 font-medium">
              <Building className="h-4 w-4 text-gray-500" aria-hidden="true" />
              {userInfo.companyName}
            </span>
            <span className="flex items-center gap-2 text-gray-700">
              <User className="h-4 w-4 text-gray-500" aria-hidden="true" />
              {userInfo.fullName}
            </span>
          </div>
        )}

        {/* Subject Selection */}
        <fieldset>
          <legend className={labelClass}>Subject</legend>
          <div className="grid grid-cols-2 gap-2">
            {subjectOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={formData.subject === option.value}
                onClick={() => handleInputChange('subject', option.value)}
                className={`${chipClass(formData.subject === option.value)} flex items-center gap-2 px-3 py-2 text-left`}
              >
                <option.icon className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
                {option.label}
              </button>
            ))}
          </div>

          {formData.subject === 'custom' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-2"
            >
              <input
                type="text"
                aria-label="Custom subject"
                placeholder="Enter custom subject"
                value={formData.customSubject}
                onChange={(e) => handleInputChange('customSubject', e.target.value)}
                className={inputClass}
              />
            </motion.div>
          )}
        </fieldset>

        {/* Priority Selection */}
        <fieldset>
          <legend className={labelClass}>Priority</legend>
          <div className="grid grid-cols-4 gap-2">
            {priorityOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={formData.priority === option.value}
                onClick={() => handleInputChange('priority', option.value)}
                className={`${chipClass(formData.priority === option.value)} flex items-center justify-center gap-1.5 px-2 py-2`}
              >
                <span className={`w-2 h-2 ${option.color} rounded-full`} aria-hidden="true" />
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Message */}
        <div>
          <label htmlFor="contact-message" className={labelClass}>Message</label>
          <textarea
            id="contact-message"
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            placeholder="Please describe your inquiry in detail..."
            rows={4}
            maxLength={1000}
            className={`${inputClass} resize-none`}
          />
          <p className="text-right mt-1 text-xs text-gray-500">{formData.message.length}/1000</p>
        </div>

        {/* File Attachment */}
        <div>
          <span className={labelClass}>Attachment (Optional)</span>
          {attachment ? (
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{attachment.name}</p>
                <p className="text-xs text-gray-500">{(attachment.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <Button variant="ghost" size="sm" ariaLabel="Remove attachment" icon={<X className="h-4 w-4" />} onClick={removeAttachment} />
            </div>
          ) : (
            <label className="block rounded-lg border-2 border-dashed border-gray-300 p-4 text-center cursor-pointer transition-colors hover:border-brand hover:bg-gray-50 focus-within:ring-2 focus-within:ring-brand">
              <input
                type="file"
                onChange={handleFileChange}
                className="sr-only"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
              />
              <Upload className="h-5 w-5 text-gray-400 mx-auto mb-1.5" aria-hidden="true" />
              <p className="text-sm text-gray-700">Click to upload file</p>
              <p className="text-xs text-gray-500">Max 10MB</p>
            </label>
          )}
        </div>

        {/* hCaptcha for Public Mode */}
        {isPublic && (
          <div className="flex justify-center">
            <HCaptcha
              sitekey={window.REACT_APP_HCAPTCHA_SITE_KEY || process.env.REACT_APP_HCAPTCHA_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
              ref={captchaRef}
              size={isMobile ? 'compact' : 'normal'}
            />
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={isSubmitting}
          disabled={isPublic && !captchaToken}
          icon={<Send className="h-4 w-4" aria-hidden="true" />}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </div>
    </form>
  );

  if (inline) return body;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
        aria-hidden="true"
      />

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-form-title"
        className="relative w-full max-w-lg max-h-[90vh] flex flex-col bg-white rounded-lg shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
          <div>
            <h2 id="contact-form-title" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="h-5 w-5 text-gray-500" aria-hidden="true" />
              Contact Sales
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Let's discuss your needs</p>
          </div>
          <Button variant="ghost" ariaLabel="Close" icon={<X className="h-5 w-5" />} onClick={onClose} />
        </div>

        {body}
      </motion.div>
    </div>
  );
};

export default ContactForm;
